import React, { useState, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import NavigationFavorite from '../NavigationFavorite';
import NavigationSearch from '../NavigationSearch';
import Button from '../Button';
import BaseContext from '../../containers/BasePage/BaseContext';
import { CookieStateContext } from '../../containers/BasePage/state';
import logoVisitSweden from '../../public/img/logotype-visitsweden.svg';

import styles from './Navigation.module.scss';

const Navigation = ({ menu = [], searchPageUrl, favoritesPageUrl, languages = [], pageType }) => {
    const { t } = useTranslation('common');
    const { isStorybook } = useContext(BaseContext);

    const [activeMenuItem, setActiveMenuItem] = useState(undefined);
    const [closingMenuItem, setClosingMenuItem] = useState(undefined);
    const [langSwitchIsOpen, setLangSwitchIsOpen] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [scrollPositionOnMenuOpen, setScrollPositionOnMenuOpen] = useState(0);

    const { state } = useContext(CookieStateContext) || {};
    const cookieConsent = state?.categories?.includes('functionality');

    const navClasses = classNames(styles['Navigation'], {
        [styles['Navigation--HomePage']]: pageType == 'HomePage',
    });

    const topBarClasses = classNames(styles['Navigation__TopBar']);

    useEffect(() => {
        const handleScroll = () => {
            // Only close menu if one is open
            if (activeMenuItem === undefined) {
                return;
            }

            // Close menu if user has scrolled 250px in either direction from when menu opened
            const scrollDelta = Math.abs(window.scrollY - scrollPositionOnMenuOpen);
            if (scrollDelta > 250) {
                closeMenu();
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [activeMenuItem, scrollPositionOnMenuOpen]);

    const closeMenu = useCallback(() => {
        if (activeMenuItem !== undefined) {
            setClosingMenuItem(activeMenuItem);
            setActiveMenuItem(undefined);
            // Clear closing state after animation completes
            setTimeout(() => {
                setClosingMenuItem(undefined);
            }, 300); // Match animation duration
        }
    }, [activeMenuItem]);

    useEffect(() => {
        const handleBlockScroll = (e) => {
            if (e.keyCode === 40 || e.keyCode === 38) {
                e.preventDefault();
            }
            // const UP = 38;
            // const DOWN = 40;
        };
        const handleKeyPress = (e) => {
            const ESC = 27;
            const UP = 38;
            const DOWN = 40;
            const currentElement = document.activeElement;
            const isButton = currentElement
                ? currentElement.classList.value.indexOf(styles['Navigation__ActiveLanguage']) > -1
                : '';
            const isLink = currentElement
                ? currentElement.classList.value.indexOf(styles['Navigation__LanguageLink']) > -1
                : '';
            const keyCode = e.keyCode;

            if (keyCode === ESC) {
                closeMenu();
                setLangSwitchIsOpen(false);
            }

            // We are at the first suggestion and press up
            if (isLink && keyCode === UP && !currentElement.previousSibling) {
                currentElement.parentElement.previousSibling.focus();
            }

            // We are not at the first suggestion and press up
            if (isLink && keyCode === UP && currentElement.previousSibling) {
                currentElement.previousSibling.focus();
            }

            // We are on the language dropdown button
            if (isButton && keyCode === DOWN) {
                currentElement.nextSibling.querySelector('a').focus();
            }

            // We press down inside dropdown
            if (isLink && keyCode === DOWN && currentElement.nextSibling) {
                currentElement.nextSibling.focus();
            }

            // We are on last item
            if (isLink && keyCode === DOWN && !currentElement.nextSibling) {
                currentElement.focus();
            }
        };

        document.addEventListener('keyup', handleKeyPress);
        if (langSwitchIsOpen) {
            document.removeEventListener('keydown', handleBlockScroll);
        }

        return () => {
            document.removeEventListener('keyup', handleKeyPress);
            if (langSwitchIsOpen) {
                document.removeEventListener('keydown', handleBlockScroll);
            }
        };
    }, [langSwitchIsOpen, closeMenu]);

    const handleClickItem = (item) => {
        setLangSwitchIsOpen(false);

        if (item === activeMenuItem) {
            closeMenu();
            return;
        }

        // Capture scroll position when opening menu
        setScrollPositionOnMenuOpen(window.scrollY);
        setClosingMenuItem(undefined); // Clear any closing state when opening new menu
        setActiveMenuItem(item);
    };

    const handleLangSwitch = () => {
        closeMenu();
        setLangSwitchIsOpen(!langSwitchIsOpen);
    };

    return (
        <div className={navClasses}>
            <div className={topBarClasses}>
                <TopBar
                    searchPageUrl={searchPageUrl}
                    favoritesPageUrl={favoritesPageUrl}
                    languages={languages}
                    activeMenuItem={activeMenuItem}
                    handleLangSwitch={handleLangSwitch}
                    langSwitchIsOpen={langSwitchIsOpen}
                    cookieConsent={cookieConsent}
                    isStorybook={isStorybook}
                    showSearch={showSearch}
                    setShowSearch={setShowSearch}
                />
            </div>
            <div className={styles['Navigation__Container']}>
                <div className={styles['Navigation__Logotype']}>
                    <a href="/" aria-label={t('navigation.logo')}>
                        <Image
                            {...logoVisitSweden}
                            overrideSrc={logoVisitSweden?.src}
                            alt="VisitSweden"
                            priority={true}
                        />
                    </a>
                    <div className={styles['Navigation__Byline']}>{t('navigation.byline')}</div>
                </div>

                <div className={styles['Navigation__MenuWrap']}>
                    <nav className={styles['Navigation__Menu']} aria-label={t('navigation.nav')}>
                        {menu.map((item, idx) => (
                            <NavItem
                                key={idx}
                                navId={`nav-id-${idx}`}
                                idx={idx}
                                onClickItem={() => {
                                    handleClickItem(item);
                                }}
                                isActive={item === activeMenuItem}
                                isClosing={item === closingMenuItem}
                                activeMenuItem={activeMenuItem}
                                {...item}
                            />
                        ))}
                    </nav>
                </div>
            </div>
        </div>
    );
};

const TopBar = ({
    searchPageUrl,
    favoritesPageUrl,
    languages,
    activeMenuItem,
    handleLangSwitch,
    langSwitchIsOpen,
    cookieConsent,
    isStorybook,
    showSearch,
    setShowSearch,
}) => {
    const serviceMenuClasses = classNames(styles['Navigation__ServiceMenu'], {
        [styles['Navigation__ServiceMenu--Fade']]: activeMenuItem !== undefined && activeMenuItem !== 'languages',
        [styles['Navigation__ServiceMenu--Active']]: activeMenuItem !== undefined && activeMenuItem === 'languages',
    });

    const handleCloseSearch = useCallback(() => setShowSearch(false), [setShowSearch]);

    const { t } = useTranslation('common');

    return (
        <div className={styles['Navigation__TopBarContent']}>
            {searchPageUrl && (
                <NavigationSearch
                    searchPageUrl={searchPageUrl}
                    isVisible={showSearch}
                    onClose={handleCloseSearch}
                    onToggle={() => setShowSearch(!showSearch)}
                    modifier="Navigation"
                />
            )}

            {favoritesPageUrl && (cookieConsent || isStorybook) && (
                <NavigationFavorite favoritesPageUrl={favoritesPageUrl} />
            )}

            <div className={serviceMenuClasses}>
                <LanguageSwitch
                    languages={languages}
                    handleLangSwitch={handleLangSwitch}
                    langSwitchIsOpen={langSwitchIsOpen}
                />
            </div>
        </div>
    );
};

const LanguageSwitch = ({ languages, handleLangSwitch, langSwitchIsOpen }) => {
    const { t } = useTranslation('common');
    const activeLanguage = languages.filter((l) => l.isActive)[0];

    const switchMenuClass = classNames(styles['Navigation__LanguageMenu'], {
        [styles['Navigation__LanguageMenu--Open']]: langSwitchIsOpen,
    });

    const languageSwitcherClasses = classNames(styles['Navigation__LanguageSwitcher'], {
        [styles['Navigation__LanguageSwitcher--Open']]: langSwitchIsOpen,
    });

    if (!activeLanguage) {
        return null;
    }

    return (
        <div className={languageSwitcherClasses}>
            <button
                onClick={handleLangSwitch}
                aria-expanded={langSwitchIsOpen}
                aria-controls="language-switch"
                className={styles['Navigation__ActiveLanguage']}>
                {t(`navigation.language`)}
            </button>
            <div id="language-switch" aria-hidden={!langSwitchIsOpen} className={switchMenuClass}>
                {languages.map((item, idx) => {
                    const languageLinkClass = classNames(styles['Navigation__LanguageLink'], {
                        [styles['Navigation__LanguageLink--Active']]: item.isActive,
                    });

                    const checkBoxClass = classNames(styles['Navigation__Checkbox'], {
                        [styles['Navigation__Checkbox--Active']]: item.isActive,
                    });

                    return (
                        <a className={languageLinkClass} key={idx} lang={item.langCode} href={item.href}>
                            <span className={checkBoxClass} aria-hidden={true} />
                            {t(`navigation.languages.${item.langCode}`)}
                        </a>
                    );
                })}
            </div>
        </div>
    );
};

const NavItem = ({ title, href, pageType, items, onClickItem, isActive, isClosing, activeMenuItem, navId }) => {
    const hasDropdown = items && items.length > 0;
    const classes = classNames(styles['Navigation__NavItem'], {
        [styles[`Navigation__NavItem--${pageType}`]]: pageType,
        [styles['Navigation__NavItem--ActivePage']]: isActive,
        [styles['Navigation__NavItem--HasDropdown']]: hasDropdown,
    });

    return (
        <>
            {items && items.length > 0 ? (
                <>
                    <button className={classes} aria-expanded={isActive} onClick={onClickItem} aria-controls={navId}>
                        <span className={styles['Navigation__NavItemText']}>{title}</span>
                    </button>
                    <Dropdown
                        navId={navId}
                        visible={isActive}
                        isClosing={isClosing}
                        items={items}
                        title={title}
                        href={href}
                        activeMenuItem={activeMenuItem}
                    />
                </>
            ) : (
                <a className={classes} href={href}>
                    <span className={styles['Navigation__NavItemText']}>{title}</span>
                </a>
            )}
        </>
    );
};

const Dropdown = ({ items, visible, isClosing, navId, title, href, activeMenuItem }) => {
    const dropdownClasses = classNames(styles['Navigation__Dropdown'], {
        [styles['Navigation__Dropdown--Visible']]: visible || isClosing,
        [styles['Navigation__Dropdown--Closing']]: isClosing,
    });

    const newItemsFiltered = items.filter((item) => item.items.length > 0);

    // Fill out so there always is at least 3 columns
    const newItems = [...newItemsFiltered, ...Array(Math.max(3 - newItemsFiltered.length, 0)).fill({ items: [] })];

    return (
        <div id={navId} className={dropdownClasses}>
            <div className={styles['Navigation__DropdownWrap']}>
                <div className={styles['Navigation__DropdownContainer']}>
                    {newItems.map((lists, index) => (
                        <div className={styles['Navigation__DropdownOuterList']} key={index}>
                            {lists.items &&
                                lists.items.map((list, listIndex) => <DropdownList {...list} key={listIndex} />)}
                        </div>
                    ))}
                    <div className={styles['Navigation__DropdownButtonWrap']}>
                        <Button type="Primary" href={href} className={styles['Navigation__DropdownButton']}>
                            <span>{title}</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DropdownList = ({ label, iconType, links }) => {
    const titleClasses = classNames(styles['Navigation__DropdownListTitle'], {
        [styles[`Navigation__DropdownListTitle--${iconType}`]]: iconType,
    });

    return (
        <div className={styles['Navigation__DropdownInnerWrap']}>
            {label && (
                <h2 className={titleClasses}>
                    {iconType && (
                        <i
                            className={styles['Navigation__DropdownListIcon']}
                            style={{
                                maskImage: `url('/img/wagtail-icons/${iconType}')`,
                            }}
                        />
                    )}
                    {label}
                </h2>
            )}

            <ul>
                {links &&
                    links.map((item, index) => {
                        return (
                            <li key={index}>
                                <a className={styles['Navigation__DropdownLink']} href={item.href}>
                                    <span>{item.title}</span>
                                </a>
                            </li>
                        );
                    })}
            </ul>
        </div>
    );
};

DropdownList.propTypes = {
    label: PropTypes.string,
    iconType: PropTypes.string,
    links: PropTypes.array,
    showArrowIcon: PropTypes.bool,
};

NavItem.propTypes = {
    idx: PropTypes.number,
    title: PropTypes.string,
    href: PropTypes.string,
    pageType: PropTypes.string,
    items: PropTypes.array,
    active: PropTypes.bool,
    isActive: PropTypes.bool,
    isClosing: PropTypes.bool,
    onClickItem: PropTypes.func,
    navId: PropTypes.string,
    activeMenuItem: PropTypes.object,
};

Dropdown.propTypes = {
    items: PropTypes.array,
    visible: PropTypes.bool,
    isClosing: PropTypes.bool,
    navId: PropTypes.string,
    title: PropTypes.string,
    href: PropTypes.string,
    activeMenuItem: PropTypes.object,
};

LanguageSwitch.propTypes = {
    languages: PropTypes.array,
    setLangSwitchIsOpen: PropTypes.func,
    langSwitchIsOpen: PropTypes.bool,
};

Navigation.propTypes = {
    menu: PropTypes.array,
    searchPageUrl: PropTypes.string,
    favoritesPageUrl: PropTypes.string,
    languages: PropTypes.array,
    pageType: PropTypes.string,
};

export default Navigation;
